import { FetchResult } from '@apollo/client'
import { ProductTypePropertiesInputType } from '@deltek/specification-client/core/Models/productType'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import {
    DisplayTextFormatType,
    ListingDisplayTextFormat,
    PtpOptionsToInclude,
} from './ProductListingDialog.types'

export const defaultProductTypeSelections = (
    hasFamily: boolean,
    family: string,
    hasProductType: boolean,
    productType: string,
    ptpOptions?: ProductTypePropertiesInputType[]
) => {
    if (hasFamily && hasProductType) {
        return [
            {
                sectionName: family,
                productTypeName: productType,
                productTypeProperties: ptpOptions?.map((ptpOption) => ({
                    propertyName: ptpOption.propertyName.toString(),
                    propertyOptionIds: ptpOption.propertyOptionIds.map(
                        (property) => property.toString().normalize('NFKC')
                    ),
                })),
            },
        ]
    }
    return []
}

interface ResolveAddListingParams {
    selectedPT: any
    isProductType: boolean
    isProductFamily: boolean
    nuggetProductFamilyId: string | undefined
    listingMatchElementId: string | null | undefined
    elementId: string | null | undefined
    listingMatchProductType: string
    displayTextFormat: DisplayTextFormatType
    withMasterSpecOption: boolean
    withManufacturerOption: boolean
}

interface ResolvedAddListingValues {
    hasSelectedPT: boolean
    isProductTypeWithNugget: boolean
    isProductFamilyOrType: boolean
    resolvedProductFamilyId: string
    resolvedProjectNuggetId: string
    resolvedProjectElementType: ProjectElementType
    resolvedProductType: string
    resolvedDisplayTextFormat: ListingDisplayTextFormat
    resolvedPtpOptionsToInclude: PtpOptionsToInclude
}

/**
 * Resolves complex conditional logic for adding a listing to a project.
 * Extracts nested ternary operations into clear, descriptive variables.
 */
export const resolveAddListingToProjectParams = (
    params: ResolveAddListingParams
): ResolvedAddListingValues => {
    const {
        selectedPT,
        isProductType,
        isProductFamily,
        nuggetProductFamilyId,
        listingMatchElementId,
        elementId,
        listingMatchProductType,
        displayTextFormat,
        withMasterSpecOption,
        withManufacturerOption,
    } = params

    // Common condition variables
    const hasSelectedPT = !!selectedPT
    const isProductTypeWithNugget = isProductType && !!nuggetProductFamilyId
    const isProductFamilyOrType = isProductFamily || isProductType

    // Determine product family ID based on context
    const resolvedProductFamilyId = hasSelectedPT
        ? selectedPT?.familyElementId ?? ''
        : isProductTypeWithNugget
        ? nuggetProductFamilyId ?? ''
        : isProductFamilyOrType
        ? listingMatchElementId ?? ''
        : ''

    // Determine project nugget ID
    const resolvedProjectNuggetId = hasSelectedPT
        ? selectedPT?.pTElementId ?? ''
        : isProductType
        ? elementId ?? ''
        : ''

    // Determine project element type
    const resolvedProjectElementType = hasSelectedPT
        ? ProjectElementType.PRODUCTTYPE
        : isProductTypeWithNugget
        ? ProjectElementType.PRODUCTTYPE
        : isProductFamilyOrType
        ? ProjectElementType.PRODUCTFAMILY
        : ProjectElementType.ASSEMBLY

    // Determine product type
    const resolvedProductType = hasSelectedPT
        ? selectedPT?.value
        : listingMatchProductType ?? ''

    // Determine display text format
    const resolvedDisplayTextFormat =
        displayTextFormat === 'Manufacturer'
            ? ListingDisplayTextFormat.Manufacturer
            : displayTextFormat === 'None'
            ? ListingDisplayTextFormat.None
            : ListingDisplayTextFormat.ManufacturerAndProduct

    // Determine PTP options to include
    const resolvedPtpOptionsToInclude =
        withMasterSpecOption && withManufacturerOption
            ? PtpOptionsToInclude.MasterSpecAndManufacturerOptions
            : withMasterSpecOption
            ? PtpOptionsToInclude.MasterSpecOptions
            : PtpOptionsToInclude.NoOptions

    return {
        hasSelectedPT,
        isProductTypeWithNugget,
        isProductFamilyOrType,
        resolvedProductFamilyId,
        resolvedProjectNuggetId,
        resolvedProjectElementType,
        resolvedProductType,
        resolvedDisplayTextFormat,
        resolvedPtpOptionsToInclude,
    }
}

export const checkPPLErrors = (
    value: FetchResult<any, Record<string, any>, Record<string, any>>
) => {
    // Check for GraphQL errors (can occur even with HTTP 200)
    // Apollo can return errors as either an array or a single Error object
    if (value.errors) {
        let errorMessage = 'An error occurred'

        // Handle errors as array
        if (Array.isArray(value.errors) && value.errors.length > 0) {
            errorMessage = value.errors[0].message
        }
        // Handle errors as single Error object
        else if (value.errors instanceof Error) {
            errorMessage = value.errors.message
        }
        // Handle errors as object with message property
        else if (
            typeof value.errors === 'object' &&
            'message' in value.errors
        ) {
            errorMessage = (value.errors as any).message
        }

        return Promise.reject(new Error(errorMessage))
    }

    // Check if data is undefined (usually indicates an error)
    if (!value.data) {
        return Promise.reject(new Error('Mutation failed - no data returned'))
    }
}

export const isProductTypeInProject = (
    productType: string,
    {
        isInProject,
        selectedProductType,
        productTypesInProject,
    }: {
        isInProject: boolean
        selectedProductType?: string | null
        productTypesInProject?: (string | null | undefined)[]
    }
): boolean => {
    const normalizedProductType = productType.toUpperCase()
    return (
        (isInProject && selectedProductType?.toUpperCase() === normalizedProductType) ||
        (productTypesInProject?.some(pt => pt?.toUpperCase() === normalizedProductType) ?? false)
    )
}

/**
 * Pure decision logic for the dialog's filter-reset gate (see usage in index.tsx).
 *
 * Returns true iff the user-set facet filters should be reset to defaults. The gate
 * keeps filters in two cases:
 *
 *   1. nodeId is unchanged — typical post-mutation re-render where only elementId
 *      flipped (e.g. listing selection echo); filters should obviously persist.
 *   2. nodeId changed but the node identity is stable — the catalog->project PT
 *      promotion case, where the node's id flips from catalog UUID to project UUID
 *      while baseElementId + treePath remain the same logical element.
 *
 * baseElementId alone is NOT a unique node key: the same catalog PT can appear under
 * multiple assemblies (same baseElementId, different ancestor chain). Pair it with
 * treePath so the gate doesn't preserve filters when the user navigates between two
 * tree instances of the same catalog product type.
 *
 * On initial mount (no previousNodeId) the gate forces a reset so the dialog opens
 * with a clean facet state.
 */
export const shouldResetDialogFilters = (input: {
    nodeId: string | null | undefined
    previousNodeId: string | null | undefined
    baseElementId: string | null | undefined
    previousBaseElementId: string | null | undefined
    treePath: string | null | undefined
    previousTreePath: string | null | undefined
}): boolean => {
    const {
        nodeId,
        previousNodeId,
        baseElementId,
        previousBaseElementId,
        treePath,
        previousTreePath,
    } = input
    if (!previousNodeId) return true
    if (previousNodeId === nodeId) return false
    const isSameLogicalNode =
        !!baseElementId &&
        !!previousBaseElementId &&
        previousBaseElementId === baseElementId &&
        !!treePath &&
        !!previousTreePath &&
        previousTreePath === treePath
    return !isSameLogicalNode
}
